import { isValidElement, useMemo } from 'react'
import { FakeLink } from '../../../FbUI/FbLink/styles'
import { FittedPillRow } from '../../../Tables/CommonTableCells/FittedPillRow'
import { PillOverflowChip } from '../../../Tables/CommonTableCells/PillOverflowChip'
import { useTableLinkVariant } from '../../../Tables/CommonTableCells/TableLinkStyleContext'
import { FilterChipZipRange } from './FilterChipZipRange'
import { FilterChipValue } from './FilterChipValue'
import {
  FilterCriteria,
  FilterStore,
  RangeFilterState,
  SearchFilterState,
  ValueFilterState,
  ZipCodeFilterState,
} from '../../FilterStore'
import { FilterChipRange } from './FilterChipRange'
import { FilterChipSearch } from './FilterChipSearch'
import { FilterIdentifier } from 'models/saved_view'
import { FILTER_TITLES } from '../../filterTitles'
import { cn } from 'components/UI/cn'

export function FilterChips({
  clearAllButton = false,
  showActive = false,
  store,
  identifier,
  className,
  excludeKeys,
}: {
  clearAllButton?: boolean
  showActive?: boolean
  store: FilterStore
  identifier: FilterIdentifier
  className?: string
  /** Filter keys to suppress from chip rendering (e.g. when a filter is
   *  already surfaced elsewhere on the page, like a segmented button group). */
  excludeKeys?: string[]
}) {
  const {
    pendingCriteria,
    criteria,
    pendingValueFilters,
    valueFilters,
    rangeFilters,
    pendingRangeFilters,
    clearPendingValueFilter,
    clearValueFilter,
    clearPendingRangeFilter,
    clearRangeFilter,
    clearAllFilters,
    clearAllPendingFilters,
    pendingZipCodeFilter,
    zipCodeFilter,
    clearPendingZipCodeFilter,
    clearZipCodeFilter,
    search,
    pendingSearch,
    clearSearch,
    clearPendingSearch,
    filterTitles,
    filterPrefixes,
    filterDecimalScales,
  } = store()

  let _valueFilters = pendingValueFilters
  let _rangeFilters = pendingRangeFilters
  let _zipCodeFilter = pendingZipCodeFilter
  let _clearValueFilter = clearPendingValueFilter
  let _clearRangeFilter = clearPendingRangeFilter
  let _search = pendingSearch
  let _clearSearch = clearPendingSearch
  let _criteria = pendingCriteria
  let _clearAll = clearAllPendingFilters
  let _clearZipCodeFilter = clearPendingZipCodeFilter

  if (showActive) {
    _valueFilters = valueFilters
    _rangeFilters = rangeFilters
    _zipCodeFilter = zipCodeFilter
    _clearValueFilter = clearValueFilter
    _clearRangeFilter = clearRangeFilter
    _search = search
    _clearSearch = clearSearch
    _criteria = criteria
    _clearAll = clearAllFilters
    _clearZipCodeFilter = clearZipCodeFilter
  }

  const excluded = new Set(excludeKeys ?? [])
  const filteredValueFilters = excluded.size
    ? Object.fromEntries(
        Object.entries(_valueFilters ?? {}).filter(([k]) => !excluded.has(k))
      )
    : _valueFilters
  const filteredRangeFilters = excluded.size
    ? Object.fromEntries(
        Object.entries(_rangeFilters ?? {}).filter(([k]) => !excluded.has(k))
      )
    : _rangeFilters

  return (
    <FilterChipsComponent
      className={className}
      filterState={{
        valueFilters: filteredValueFilters,
        rangeFilters: filteredRangeFilters,
        zipCodeFilter: _zipCodeFilter,
        search: _search,
        criteria: _criteria,
      }}
      actions={{
        clearValueFilter: _clearValueFilter,
        clearSearch: _clearSearch,
        clearAll: clearAllButton ? _clearAll : undefined,
        clearZipCodeFilter: _clearZipCodeFilter,
        clearRangeFilter: _clearRangeFilter,
      }}
      identifier={identifier}
      filterTitles={filterTitles}
      filterPrefixes={filterPrefixes}
      filterDecimalScales={filterDecimalScales}
    />
  )
}

export function FilterChipsComponent({
  filterState,
  actions,
  identifier,
  className,
  filterTitles,
  filterPrefixes,
  filterDecimalScales,
  isHovered,
  collapseToFit = false,
}: {
  filterState: {
    valueFilters?: ValueFilterState
    rangeFilters?: RangeFilterState
    zipCodeFilter?: ZipCodeFilterState
    search?: SearchFilterState
    criteria?: FilterCriteria
  }
  actions?: {
    clearZipCodeFilter?: () => void
    clearRangeFilter?: (key: string) => void
    clearValueFilter?: (key: string) => void
    clearSearch?: (key: string) => void
    clearAll?: () => void
  }
  identifier: FilterIdentifier
  className?: string
  filterTitles?: Record<string, string>
  /** Per-key currency / unit prefix registry — populated by RangeFilter
   *  when its `prefix` prop is set so chips can render "$15K" instead of
   *  "15K" for money-typed range filters. */
  filterPrefixes?: Record<string, string>
  /** Per-key decimal precision registry — populated by RangeFilter when
   *  its `decimalScale` prop is > 0 so currency chips preserve cents
   *  ("$50,500.50" instead of toLocaleString's default "$50,500.5"). */
  filterDecimalScales?: Record<string, number>
  isHovered?: boolean
  /** Opt into the single-line "fit as many chips as the width allows + a click
   *  popover for the rest" layout. Only the Saved Filters table cell (fixed,
   *  resizable column) wants this; the page toolbars keep the wrapping layout
   *  so "Clear all" never truncates and the row keeps its spacing. */
  collapseToFit?: boolean
}) {
  const totalFilterCount = useMemo(
    () =>
      Object.keys(filterState?.valueFilters ?? {}).length +
      Object.keys(filterState?.rangeFilters ?? {}).length +
      Object.values(filterState?.search ?? {}).filter((s) => !!s.value).length +
      (filterState?.zipCodeFilter?.[0] ? 1 : 0),
    [filterState]
  )

  const variant = useTableLinkVariant()

  if (totalFilterCount === 0) return null

  const handleClearZipCodeFilter = () => {
    if (totalFilterCount === 1) {
      actions?.clearAll?.()
      return
    }

    actions?.clearZipCodeFilter?.()
  }

  const handleClearRangeFilter = (key: string) => {
    if (totalFilterCount === 1) {
      actions?.clearAll?.()
      return
    }

    actions?.clearRangeFilter?.(key)
  }

  const handleClearValueFilter = (key: string) => {
    if (totalFilterCount === 1) {
      actions?.clearAll?.()
      return
    }

    actions?.clearValueFilter?.(key)
  }

  const handleClearSearch = (key: string) => {
    if (totalFilterCount === 1) {
      actions?.clearAll?.()
      return
    }

    actions?.clearSearch?.(key)
  }

  // Build the chips once as an ordered array so a DS2026 (plain) surface can
  // fit-as-many-as-possible + "+N" (with a click popover for the rest) while
  // other surfaces keep the original flex-wrap.
  const pills: React.ReactNode[] = []
  if (filterState?.zipCodeFilter?.[0]) {
    pills.push(
      <FilterChipZipRange
        key="zip"
        label="Zip Code"
        active={filterState.zipCodeFilter}
        isExclusive={filterState.criteria === 'neither'}
        isHovered={isHovered}
        onClose={
          actions?.clearZipCodeFilter ? handleClearZipCodeFilter : undefined
        }
      />
    )
  }
  Object.entries(filterState?.rangeFilters ?? {}).forEach(([key, value]) => {
    pills.push(
      <FilterChipRange
        key={`range-${key}`}
        filterCriteria={filterState?.criteria ?? 'all'}
        title={filterTitles?.[key] || FILTER_TITLES?.[identifier]?.[key] || key}
        // Pulled from the per-store prefix registry that RangeFilter
        // populates when its `prefix` prop is set, so $-denominated
        // money chips read "Lifetime Value: $15K – $80K".
        prefix={filterPrefixes?.[key]}
        decimalScale={filterDecimalScales?.[key]}
        isHovered={isHovered}
        onClose={
          actions?.clearRangeFilter
            ? () => handleClearRangeFilter(key)
            : undefined
        }
        value={value}
      />
    )
  })
  Object.entries(filterState?.valueFilters ?? {}).forEach(([key, value]) => {
    pills.push(
      <FilterChipValue
        key={`value-${key}`}
        filterCriteria={filterState?.criteria ?? 'all'}
        title={filterTitles?.[key] || FILTER_TITLES?.[identifier]?.[key] || key}
        isHovered={isHovered}
        onClose={
          actions?.clearValueFilter
            ? () => handleClearValueFilter(key)
            : undefined
        }
        value={value}
      />
    )
  })
  Object.keys(filterState?.search ?? {}).forEach((key) => {
    pills.push(
      <FilterChipSearch
        key={`search-${key}`}
        filterCriteria={filterState?.criteria ?? 'all'}
        search={filterState?.search?.[key]?.value ?? ''}
        searchLabel={filterState?.search?.[key]?.label ?? ''}
        isHovered={isHovered}
        onClose={
          actions?.clearSearch ? () => handleClearSearch(key) : undefined
        }
      />
    )
  })

  const clearAll =
    actions?.clearAll && totalFilterCount > 0 ? (
      <FakeLink className={'ml-2'} onClick={actions?.clearAll}>
        Clear all
      </FakeLink>
    ) : null

  if (variant === 'plain' && collapseToFit) {
    const itemsKey = pills
      .map((pill) => (isValidElement(pill) ? String(pill.key) : ''))
      .join(',')
    return (
      <div className={cn('flex w-full min-w-0 items-center gap-2', className)}>
        <FittedPillRow
          key={itemsKey}
          pills={pills}
          renderOverflow={(hidden, count) => (
            <PillOverflowChip count={count}>{hidden}</PillOverflowChip>
          )}
        />
        {clearAll}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'd-flex flex-wrap align-items-center gap-2 ml-auto mt-2.5',
        className
      )}
    >
      {pills}
      {clearAll}
    </div>
  )
}
